import React, { useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { PhotoIcon, AlbumIcon, ShareIcon, SettingsIcon } from './Icons';
import { FiShield, FiDroplet, FiTag, FiUsers, FiCamera, FiLogOut, FiInbox, FiArchive } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import useStripBadge from '../utils/useStripBadge';
import { getFeatureFlags } from '../utils/featureFlags';

export default function AppRail() {
  const { user, logout, loadingMessage, isAuthenticating } = useAuth();
  const location = useLocation();
  const photosActive = location.pathname === '/' || location.pathname.startsWith('/photos');
  const features = getFeatureFlags(user);
  const { hasNew: hasStripNew, markSeen: markStripSeen } = useStripBadge(user?.id, { enabled: features.strip });

  useEffect(() => {
    if (features.strip && location.pathname.startsWith('/strip')) {
      markStripSeen();
    }
  }, [location.pathname, markStripSeen, features.strip]);

  if (!user) return null;

  const linkClass = (isActive) =>
    `rail-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-[#304849] text-[#fcefb3]'
        : 'text-[#3f5150] hover:bg-[#eef4f2] hover:text-[#16211f]'
    }`;

  return (
    <aside className="hidden lg:flex flex-col w-56 bg-[#fdf8e2] border border-[#efe4b6] rounded-2xl sticky top-3 ml-3 my-3 h-[calc(100vh-1.5rem)] overflow-hidden">
      {/* Logo */}
      <div className="p-4 flex justify-center">
        <Link to="/" aria-label="Photo Harbor Home">
          <img src="/brand/logo-primary-darkteal.png" alt="Photo Harbor" className="h-36 w-auto" />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-2 space-y-1">
        <NavLink to="/photos" className={() => linkClass(photosActive)}>
          <PhotoIcon size={18} />
          <span>Photos</span>
        </NavLink>
        <NavLink to="/albums" className={({ isActive }) => linkClass(isActive)}>
          <AlbumIcon size={18} />
          <span>Albums</span>
        </NavLink>
        <NavLink to="/tags" className={({ isActive }) => linkClass(isActive)}>
          <FiTag size={18} />
          <span>Tags</span>
        </NavLink>
        {features.friends && (
          <NavLink to="/friends" className={({ isActive }) => linkClass(isActive)}>
            <FiUsers size={18} />
            <span>Friends/Team</span>
          </NavLink>
        )}
        {features.strip && (
          <NavLink to="/strip" className={({ isActive }) => linkClass(isActive)}>
            <FiCamera size={18} />
            <span>The Strip</span>
            {hasStripNew && (
              <span className="strip-new-badge strip-new-badge--inline" aria-label="New items">
                New
              </span>
            )}
          </NavLink>
        )}
        {features.shares && (
          <NavLink to="/shares" className={({ isActive }) => linkClass(isActive)}>
            <ShareIcon size={18} />
            <span>Shares</span>
          </NavLink>
        )}
        {features.clients && (
          <NavLink to="/submissions" className={({ isActive }) => linkClass(isActive)}>
            <FiInbox size={18} />
            <span>Jobs</span>
          </NavLink>
        )}
        <NavLink to="/archive" className={({ isActive }) => linkClass(isActive)}>
          <FiArchive size={18} />
          <span>Scan Archive</span>
        </NavLink>
        <NavLink to="/watermark" className={({ isActive }) => linkClass(isActive)}>
          <FiDroplet size={18} />
          <span>Watermark</span>
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => linkClass(isActive)}>
          <SettingsIcon size={18} />
          <span>Settings</span>
        </NavLink>
        {user && user.role === 'super_admin' && (
          <NavLink to="/admin" className={({ isActive }) => linkClass(isActive)}>
            <FiShield size={18} />
            <span>Admin</span>
          </NavLink>
        )}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-[#efe4b6]">
        <button
          onClick={logout}
          disabled={isAuthenticating}
          className="rail-link flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-[#3f5150] hover:bg-[#eef4f2] hover:text-[#16211f] transition-colors disabled:opacity-50"
        >
          {isAuthenticating ? (
            <span className="w-[18px] h-[18px] border-2 border-[#c3cecd] border-t-[#304849] rounded-full animate-spin" />
          ) : (
            <FiLogOut size={18} />
          )}
          <span>{isAuthenticating ? (loadingMessage || 'Signing out...') : 'Sign out'}</span>
        </button>
      </div>
    </aside>
  );
}
